import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronLeft, ChevronRight, Trash2, Save, X, Mail, Phone, Building2, MessageSquare, Tag, Calendar, User, Briefcase, Globe } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import PortalLayout from '../../../components/PortalLayout';
import axios from 'axios';

const LeadManagement = () => {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAdvisor, setFilterAdvisor] = useState('all');
  const [filterTopic, setFilterTopic] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 5;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchLeads();
    fetchUsers();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/leads/inbound`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data.leads.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const saveLead = async () => {
    try {
      // Convert "unassigned" to empty string for backend
      const dataToSave = {
        ...editData,
        assigned_to: editData.assigned_to === 'unassigned' ? '' : editData.assigned_to
      };
      
      await axios.put(`${BACKEND_URL}/api/portal/leads/inbound/${selectedLead.id}/assign`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditMode(false);
      fetchLeads();
      const updated = leads.find(l => l.id === selectedLead.id);
      if (updated) setSelectedLead({...updated, ...dataToSave});
    } catch (error) {
      alert('Error saving lead');
    }
  };

  const deleteLead = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/leads/inbound/${selectedLead.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteDialog(false);
      setSelectedLead(null);
      fetchLeads();
    } catch (error) {
      alert('Error deleting lead');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = (lead.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (lead.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    const matchesAdvisor = filterAdvisor === 'all' || 
                          (filterAdvisor === 'unassigned' && !lead.assigned_to) ||
                          lead.assigned_to === filterAdvisor;
    const matchesTopic = filterTopic === 'all' || lead.topic === filterTopic;
    return matchesSearch && matchesStatus && matchesAdvisor && matchesTopic;
  });

  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  const paginatedLeads = filteredLeads.slice((currentPage - 1) * leadsPerPage, currentPage * leadsPerPage);
  const topics = [...new Set(leads.map(l => l.topic).filter(Boolean))];

  return (
    <PortalLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Inbound Leads</h1>
          <p className="text-gray-600">View and manage contact form submissions</p>
        </div>

        <div className="bg-white rounded-xl p-4 mb-6 border border-gray-200">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAdvisor} onValueChange={setFilterAdvisor}>
              <SelectTrigger>
                <SelectValue placeholder="All advisors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Advisors</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.filter(u => u.job_role).map(u => (
                  <SelectItem key={u.id} value={u.email}>{u.first_name} {u.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterTopic} onValueChange={setFilterTopic}>
              <SelectTrigger>
                <SelectValue placeholder="All topics" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Topics</SelectItem>
                {topics.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
          <table className="w-full table-fixed">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="w-1/4 px-4 py-3 text-left text-xs font-semibold text-gray-900">Company</th>
                <th className="w-1/4 px-4 py-3 text-left text-xs font-semibold text-gray-900">Contact</th>
                <th className="w-1/5 px-4 py-3 text-left text-xs font-semibold text-gray-900">Solutions Advisor</th>
                <th className="w-1/6 px-4 py-3 text-left text-xs font-semibold text-gray-900">Topic</th>
                <th className="w-1/6 px-4 py-3 text-left text-xs font-semibold text-gray-900">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {paginatedLeads.map((lead) => (
                <tr key={lead.id} onClick={() => { setSelectedLead(lead); setEditData({ assigned_to: lead.assigned_to, status: lead.status }); setEditMode(false); }} className="hover:bg-blue-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 truncate">{lead.company}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 truncate">{lead.first_name} {lead.last_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 truncate">{lead.assigned_to || <span className="text-gray-400">Unassigned</span>}</td>
                  <td className="px-4 py-3"><span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">{lead.topic}</span></td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(lead.created_at).toLocaleDateString('en-GB')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mb-6">
            <Button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} variant="outline" size="sm"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
            <Button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} variant="outline" size="sm"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}

        {selectedLead && (
          <div className="bg-white rounded-xl border-2 border-blue-300 shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Lead Details</h2>
              <div className="flex gap-2">
                {editMode ? (
                  <><Button onClick={saveLead} className="bg-green-600 hover:bg-green-700"><Save className="w-4 h-4 mr-2" />Save</Button><Button onClick={() => setEditMode(false)} variant="outline">Cancel</Button></>
                ) : (
                  <><Button onClick={() => setEditMode(true)} variant="outline">Edit</Button><Button onClick={() => setShowDeleteDialog(true)} variant="outline" className="border-red-300 text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</Button></>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
                <div><p className="text-xs text-gray-500">Email</p><a href={`mailto:${selectedLead.email}`} className="text-sm font-semibold text-blue-600 hover:underline">{selectedLead.email}</a></div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-blue-600" />
                <div><p className="text-xs text-gray-500">Phone</p><p className="text-sm font-semibold text-gray-900">{selectedLead.phone || 'Not provided'}</p></div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
                <div><p className="text-xs text-gray-500">Company</p><p className="text-sm font-semibold text-gray-900">{selectedLead.company}</p></div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Briefcase className="w-5 h-5 text-blue-600" />
                <div><p className="text-xs text-gray-500">Role</p><p className="text-sm font-semibold text-gray-900">{selectedLead.role || 'Not specified'}</p></div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-1"><Tag className="w-4 h-4 text-purple-600" /><p className="text-xs font-semibold text-gray-700">Topic</p></div>
                <p className="text-sm text-gray-900">{selectedLead.topic || 'General Enquiry'}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-1"><Globe className="w-4 h-4 text-blue-600" /><p className="text-xs font-semibold text-gray-700">Source</p></div>
                <p className="text-sm text-gray-900">{selectedLead.source || 'Contact form'}</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-1"><User className="w-4 h-4 text-green-600" /><p className="text-xs font-semibold text-gray-700">Solutions Advisor</p></div>
                {editMode ? (
                  <Select value={editData.assigned_to || ''} onValueChange={(value) => setEditData({...editData, assigned_to: value})}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Assign advisor..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.filter(u => u.job_role).map(u => (
                        <SelectItem key={u.id} value={u.email}>{u.first_name} {u.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-gray-900">{selectedLead.assigned_to || 'Unassigned'}</p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-3"><MessageSquare className="w-5 h-5 text-gray-600" /><h3 className="font-bold text-gray-900">Message</h3></div>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedLead.message}</p>
            </div>
          </div>
        )}

        {showDeleteDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Delete Lead?</h2>
              <p className="text-gray-700 mb-2">Delete lead from <strong>{selectedLead?.first_name} {selectedLead?.last_name}</strong> at <strong>{selectedLead?.company}</strong>?</p>
              <p className="text-sm text-red-600 mb-6">This action cannot be undone.</p>
              <div className="flex gap-3"><Button onClick={deleteLead} className="flex-1 bg-red-600 hover:bg-red-700">Delete</Button><Button onClick={() => setShowDeleteDialog(false)} variant="outline" className="flex-1">Cancel</Button></div>
            </motion.div>
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default LeadManagement;
